/**
 * Email Step Component
 * 
 * Handles email input and validation for both login and signup flows.
 * Memoized for performance optimization.
 */

import { memo } from "react";

import { Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";


import type { AuthState, AuthAction } from "@/types/state";

interface EmailStepProps {
  state: AuthState;
  dispatch: (action: AuthAction) => void;
  onSubmit: () => void;
  onSwitchMode: () => void;
}

const EmailStepComponent = ({ state, dispatch, onSubmit, onSwitchMode }: EmailStepProps) => {
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    dispatch({ type: 'SET_EMAIL', payload: email });
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    dispatch({
      type: 'SET_EMAIL_VALIDATION',
      payload: {
        valid: isValid,
        error: email && !isValid ? 'Please enter a valid email address' : null
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch({ type: 'SET_EMAIL_TOUCHED', payload: true });
    
    if (state.emailValid) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            value={state.email}
            onChange={handleEmailChange}
            onBlur={() => dispatch({ type: 'SET_EMAIL_TOUCHED', payload: true })}
            disabled={state.loading || state.step === 'validatingEmail'}
            className="pl-10"
            autoComplete="email"
            autoFocus
          />
        </div>
        {state.emailTouched && state.emailError && (
          <p className="text-sm text-destructive">{state.emailError}</p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!state.emailValid || state.loading || state.step === 'validatingEmail'}
      >
        {state.step === 'validatingEmail' ? 'Validating...' : 'Continue'}
      </Button>

      <div className="text-center text-sm">
        <button
          type="button"
          onClick={onSwitchMode}
          className="text-primary hover:underline"
          disabled={state.loading}
        >
          {state.mode === 'login' 
            ? "Don't have an account? Sign up" 
            : 'Already have an account? Sign in'}
        </button>
      </div>
    </form>
  );
};

export const EmailStep = memo(EmailStepComponent);
